import { useState, useRef, useEffect, FormEvent } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ACCOUNT_UNVERIFIED_ERROR, resendVerificationEmail, forgotPassword } from '../../api/auth';
import { validateEmail } from '../../utils/validation';
import { Spinner } from '../Spinner';

interface LoginModalProps {
  onClose: () => void;
  triggerRef: React.RefObject<HTMLElement | null>;
}

type ModalMode = 'login' | 'forgot-password';

export function LoginModal({ onClose, triggerRef }: LoginModalProps) {
  const { login } = useAuth();
  const [mode, setMode] = useState<ModalMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Focus trap + keyboard handling
  useEffect(() => {
    // Lock body scroll
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus first input
    firstInputRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      // Focus trap
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'input, button, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      triggerRef.current?.focus();
    };
  }, [onClose, triggerRef]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setResendMessage('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setIsLoading(true);
    const loginError = await login(email, password, rememberMe);
    if (loginError) {
      setError(loginError);
      setIsLoading(false);
    }
    // On success, AuthContext navigates automatically
  };

  const handleResend = async () => {
    setResendMessage('');
    setIsResending(true);
    const { error: resendError } = await resendVerificationEmail(email);
    setIsResending(false);
    setResendMessage(resendError || 'Verification email sent.');
  };

  const handleForgotPasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotMessage('');

    if (!validateEmail(forgotEmail)) {
      setForgotError('Please enter a valid email address');
      return;
    }

    setIsSendingReset(true);
    const { error: sendError } = await forgotPassword(forgotEmail);
    setIsSendingReset(false);

    if (sendError) {
      setForgotError(sendError);
      return;
    }

    setForgotMessage('If an account exists for this email, a password reset link has been sent.');
  };

  const switchToForgotPassword = () => {
    setForgotEmail(email);
    setForgotError('');
    setForgotMessage('');
    setError('');
    setResendMessage('');
    setMode('forgot-password');
  };

  const switchToLogin = () => {
    setForgotError('');
    setForgotMessage('');
    setMode('login');
  };

  const handleScrollToRegister = () => {
    onClose();
    document.getElementById('hero')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ background: 'rgba(10, 9, 8, 0.8)', backdropFilter: 'blur(8px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-modal-title"
    >
      <div
        ref={modalRef}
        className="relative w-full max-w-[400px] mx-4 rounded-lg"
        style={{
          background: '#1c1916',
          border: '1px solid #2e2924',
          padding: '36px 32px',
        }}
      >
        {/* Blue accent line */}
        <div
          className="absolute top-[-1px] left-6 right-6 h-[2px]"
          style={{ background: 'linear-gradient(90deg, transparent, #6a9e8e, transparent)' }}
        />

        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1 transition-colors hover:text-game-200"
          style={{ background: 'none', border: 'none', color: '#635850', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}
          aria-label="Close login"
        >
          &times;
        </button>

        <h2
          id="login-modal-title"
          className="text-center mb-6"
          style={{ fontFamily: "'Cinzel', serif", fontSize: 20, fontWeight: 600, color: '#f0e8dc' }}
        >
          {mode === 'login' ? 'Welcome Back' : 'Reset Your Password'}
        </h2>

        {mode === 'forgot-password' && (
          <form onSubmit={handleForgotPasswordSubmit}>
            <p className="mb-4" style={{ color: '#b5a99c', fontSize: 14 }}>
              Enter your email and we&apos;ll send you a link to reset your password.
            </p>
            <div className="mb-4">
              <label
                htmlFor="forgot-password-email"
                className="block mb-1.5 uppercase tracking-wide"
                style={{ fontFamily: "'Cinzel', serif", fontSize: 12, fontWeight: 500, color: '#b5a99c' }}
              >
                Email
              </label>
              <input
                id="forgot-password-email"
                type="email"
                placeholder="player@example.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                disabled={isSendingReset}
                className="w-full px-4 py-3 rounded focus:outline-none transition-colors"
                style={{
                  background: '#0a0908',
                  border: '1px solid #2e2924',
                  color: '#f0e8dc',
                  fontSize: 16,
                }}
              />
            </div>

            {forgotError && (
              <div className="mb-4 px-3 py-2 rounded text-sm" style={{ background: '#2a1414', border: '1px solid #4a2020', color: '#ff5555' }}>
                {forgotError}
              </div>
            )}
            {forgotMessage && (
              <div className="mb-4 px-3 py-2 rounded text-sm" style={{ background: '#14241a', border: '1px solid #205a3a', color: '#6a9e8e' }}>
                {forgotMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={isSendingReset}
              className="w-full py-3 rounded font-semibold transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #2a4a4a, #1e3a3a)',
                color: '#fff',
                fontFamily: "'Cinzel', serif",
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: '0.06em',
                border: '1px solid rgba(106,158,142, 0.3)',
                cursor: isSendingReset ? 'not-allowed' : 'pointer',
              }}
            >
              {isSendingReset ? <><Spinner size="sm" /> Sending...</> : 'Send reset link'}
            </button>

            <div className="text-center mt-4" style={{ color: '#635850', fontSize: 13 }}>
              <button
                type="button"
                onClick={switchToLogin}
                className="hover:underline"
                style={{ color: '#6a9e8e', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
              >
                Back to login
              </button>
            </div>
          </form>
        )}

        {mode === 'login' && (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label
                htmlFor="login-email"
                className="block mb-1.5 uppercase tracking-wide"
                style={{ fontFamily: "'Cinzel', serif", fontSize: 12, fontWeight: 500, color: '#b5a99c' }}
              >
                Email
              </label>
              <input
                ref={firstInputRef}
                id="login-email"
                type="email"
                placeholder="player@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                className="w-full px-4 py-3 rounded focus:outline-none transition-colors"
                style={{
                  background: '#0a0908',
                  border: '1px solid #2e2924',
                  color: '#f0e8dc',
                  fontSize: 16,
                }}
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor="login-password"
                className="block mb-1.5 uppercase tracking-wide"
                style={{ fontFamily: "'Cinzel', serif", fontSize: 12, fontWeight: 500, color: '#b5a99c' }}
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-4 py-3 rounded focus:outline-none transition-colors"
                  style={{
                    background: '#0a0908',
                    border: '1px solid #2e2924',
                    color: '#f0e8dc',
                    fontSize: 16,
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 transition-colors hover:text-game-300"
                  style={{ background: 'none', border: 'none', color: '#635850', cursor: 'pointer' }}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              <div className="text-right mt-1.5">
                <button
                  type="button"
                  onClick={switchToForgotPassword}
                  className="hover:underline"
                  style={{ color: '#6a9e8e', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2 mb-4 cursor-pointer" style={{ color: '#b5a99c', fontSize: 14 }}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={isLoading}
              />
              Remember me for 48 hours
            </label>

            {error && error === ACCOUNT_UNVERIFIED_ERROR && (
              <div className="mb-4 px-3 py-2 rounded text-sm" style={{ background: '#2a1414', border: '1px solid #4a2020', color: '#ff5555' }}>
                Please verify your email before signing in.{' '}
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isResending}
                  className="hover:underline"
                  style={{ color: '#ff8080', background: 'none', border: 'none', cursor: isResending ? 'not-allowed' : 'pointer', padding: 0, font: 'inherit' }}
                >
                  {isResending ? 'Sending…' : 'Resend verification email'}
                </button>
              </div>
            )}

            {error && error !== ACCOUNT_UNVERIFIED_ERROR && (
              <div className="mb-4 px-3 py-2 rounded text-sm" style={{ background: '#2a1414', border: '1px solid #4a2020', color: '#ff5555' }}>
                {error}
              </div>
            )}

            {resendMessage && (
              <div className="mb-4 px-3 py-2 rounded text-sm" style={{ background: '#14241a', border: '1px solid #205a3a', color: '#6a9e8e' }}>
                {resendMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 rounded font-semibold transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #2a4a4a, #1e3a3a)',
                color: '#fff',
                fontFamily: "'Cinzel', serif",
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: '0.06em',
                border: '1px solid rgba(106,158,142, 0.3)',
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {isLoading ? <><Spinner size="sm" /> Logging in...</> : 'Log In'}
            </button>
          </form>
        )}

        {mode === 'login' && (
          <div className="text-center mt-4" style={{ color: '#635850', fontSize: 13 }}>
            Don&apos;t have an account?{' '}
            <button
              onClick={handleScrollToRegister}
              className="hover:underline"
              style={{ color: '#6a9e8e', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
            >
              Register above
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
